import { Component, inject, signal, DestroyRef, OnInit, OnDestroy, NgZone, ElementRef, Injector, runInInjectionContext } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { NzBackTopModule } from 'ng-zorro-antd/back-top';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { AuthService } from './pages/auth/services/auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkScrollable, ScrollDispatcher } from '@angular/cdk/scrolling';
import { Directionality } from '@angular/cdk/bidi';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil, throttleTime } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, NzBackTopModule, NzSpinModule],
  template: `
    <nz-back-top></nz-back-top>
    <div class="full-height">
      <router-outlet></router-outlet>
    </div>
    @if (loading()) {
      <div
        class="fixed top-0 left-0 w-full h-full z-[1001]"
        style="background:rgba(24,144,255,0.1);"
      >
        <div class="absolute top-1/2 left-1/2 -mt-4 -ml-4">
          <nz-spin nzSize="large"></nz-spin>
        </div>
      </div>
    }
  `,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'HIS Web Application';
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private injector = inject(Injector);
  private scrollDispatcher = inject(ScrollDispatcher);
  private dir = inject(Directionality, { optional: true });

  loading = signal(false);
  
  private observer: MutationObserver | null = null;
  private registeredElements = new Map<Element, { scrollable: CdkScrollable; destroy$: Subject<void> }>();

  constructor() {
    // Subscribe to loading state and update signal
    this.authService.isLoading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => this.loading.set(loading));
  }

  ngOnInit(): void {
    this.setupModalScrollFix();
  }

  ngOnDestroy(): void {
    this.cleanupModalScrollFix();
  }

  /**
   * Setup MutationObserver to automatically register modal bodies with CdkScrollable.
   * This fixes the issue where nz-select dropdown doesn't follow input when scrolling in modal.
   */
  private setupModalScrollFix(): void {
    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              this.processAddedNode(node);
            }
          });
          mutation.removedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              this.processRemovedNode(node);
            }
          });
        });
      });

      this.observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    });
  }

  private processAddedNode(element: HTMLElement): void {
    setTimeout(() => {
      const containers = this.findScrollableContainers(element);
      containers.forEach((container) => {
        if (!this.registeredElements.has(container)) {
          this.registerScrollableContainer(container as HTMLElement);
        }
      });
    }, 50);
  }

  private processRemovedNode(element: HTMLElement): void {
    const containers = this.findScrollableContainers(element);
    containers.forEach((container) => {
      this.unregisterScrollableContainer(container as HTMLElement);
    });
    
    if (this.registeredElements.has(element)) {
      this.unregisterScrollableContainer(element);
    }
  }

  private findScrollableContainers(element: HTMLElement): Element[] {
    const result: Element[] = [];
    const selectors = '.ant-modal-body, .ant-drawer-body';
    
    if (element.matches?.(selectors)) {
      result.push(element);
    }
    
    result.push(...Array.from(element.querySelectorAll(selectors)));
    return result;
  }

  private registerScrollableContainer(element: HTMLElement): void {
    const destroy$ = new Subject<void>();
    
    // Create CdkScrollable within injection context
    runInInjectionContext(this.injector, () => {
      const elementRef = new ElementRef<HTMLElement>(element);
      const cdkScrollable = new CdkScrollable(
        elementRef,
        this.scrollDispatcher,
        this.ngZone,
        this.dir || undefined
      );
      
      // Initialize CdkScrollable
      cdkScrollable.ngOnInit();
      
      this.registeredElements.set(element, { scrollable: cdkScrollable, destroy$ });
      
      // Also manually emit scroll events to ScrollDispatcher's internal subject
      this.ngZone.runOutsideAngular(() => {
        fromEvent(element, 'scroll', { passive: true })
          .pipe(
            throttleTime(5), // Faster response for smoother scrolling
            takeUntil(destroy$)
          )
          .subscribe(() => {
            // Force emit scroll event through ScrollDispatcher
            const scrollDispatcherAny = this.scrollDispatcher as any;
            if (scrollDispatcherAny._scrolled) {
              scrollDispatcherAny._scrolled.next(cdkScrollable);
            }
            
            // Check if open select inputs are still visible in the modal
            this.closeDropdownsOutsideViewport(element);
          });
      });
    });
  }

  /**
   * Close dropdowns when their trigger elements are scrolled outside the visible viewport of the modal
   */
  private closeDropdownsOutsideViewport(scrollContainer: HTMLElement): void {
    const containerRect = scrollContainer.getBoundingClientRect();
    const openSelects = document.querySelectorAll('nz-select.ant-select-open');
    
    openSelects.forEach((select) => {
      const selectRect = select.getBoundingClientRect();
      
      // Check if the select is outside the visible area of the scroll container
      // Add some buffer (e.g., 20px) for smoother experience
      const isAboveViewport = selectRect.bottom < containerRect.top + 20;
      const isBelowViewport = selectRect.top > containerRect.bottom - 20;
      
      if (isAboveViewport || isBelowViewport) {
        // Immediately hide the dropdown overlay for instant visual feedback
        const dropdownOverlays = document.querySelectorAll('.cdk-overlay-pane');
        const hiddenOverlays: HTMLElement[] = [];
        
        dropdownOverlays.forEach((overlay) => {
          const overlayEl = overlay as HTMLElement;
          // Check if this overlay contains select dropdown
          if (overlayEl.querySelector('.ant-select-dropdown')) {
            overlayEl.style.visibility = 'hidden';
            overlayEl.style.opacity = '0';
            hiddenOverlays.push(overlayEl);
          }
        });
        
        // First, blur the select to prevent space key from reopening
        const selectInput = select.querySelector('input, .ant-select-selection-search-input') as HTMLElement;
        if (selectInput) {
          selectInput.blur();
        }
        
        // Also blur the nz-select itself
        (select as HTMLElement).blur();
        
        // Close the dropdown by dispatching Escape key
        select.dispatchEvent(new KeyboardEvent('keydown', {
          key: 'Escape',
          code: 'Escape',
          keyCode: 27,
          bubbles: true,
          cancelable: true
        }));
        
        // Reset the overlay styles after a short delay so next open works correctly
        setTimeout(() => {
          hiddenOverlays.forEach((overlayEl) => {
            overlayEl.style.visibility = '';
            overlayEl.style.opacity = '';
          });
        }, 800);
      }
    });
  }

  private unregisterScrollableContainer(element: HTMLElement): void {
    const registered = this.registeredElements.get(element);
    if (registered) {
      registered.destroy$.next();
      registered.destroy$.complete();
      registered.scrollable.ngOnDestroy();
      this.registeredElements.delete(element);
    }
  }

  private cleanupModalScrollFix(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    
    this.registeredElements.forEach((registered) => {
      registered.destroy$.next();
      registered.destroy$.complete();
      registered.scrollable.ngOnDestroy();
    });
    this.registeredElements.clear();
  }
}
